import { resolveDocPath } from "@minga/protocol/doc-path"
import { createContext, useContext } from "react"

/**
 * Wave DOC (decision 0068, spec §5.2): relative links and images inside a project document. Markdown renders a
 * document with a `DocLinks` provider; everywhere else (chat, items, transcripts) there is none and links and images
 * keep today's handling (images never render). With the provider:
 * - a relative link that resolves (from the document's path) to a live document opens its page in the app; one that
 *   resolves to nothing, or leaves the folder root, is plain text titled "not imported";
 * - an image renders only when its relative `src` resolves to a live image document, from that document's signed
 *   URL in `<img>` (an SVG's scripts never run); a remote `src` shows "Remote image not shown: <host/path>" (remote
 *   images stay blocked); any other (absolute, missing) shows "image not imported: <path>".
 * Absolute and external links (`/…`, `https:`, `#top`) keep today's handling (decision 0029).
 */

export type DocLinkResolution =
  /** A live document: its page (`/w/…/o/O-14#hash`). */
  | { readonly kind: "page"; readonly href: string }
  /** Relative, but no live document there (or it leaves the root). */
  | { readonly kind: "missing"; readonly path: string }

export type DocImageResolution =
  | { readonly kind: "image"; readonly src: string }
  /** A live image document whose signed URL is still loading. */
  | { readonly kind: "loading" }
  /** A remote image (`https://…`): never loaded, it would tell its host who reads the page. `shown`: host and path. */
  | { readonly kind: "remote"; readonly shown: string }
  | { readonly kind: "missing"; readonly path: string }

export interface DocLinks {
  /** Null: not a relative link (absolute, external, an in-page `#anchor`), so today's handling applies. */
  readonly resolve: (href: string) => DocLinkResolution | null
  readonly image: (src: string) => DocImageResolution
}

export const DocLinksContext = createContext<DocLinks | null>(null)

/** The document's links, or null outside a document. */
export const useDocLinks = (): DocLinks | null => useContext(DocLinksContext)

const ABSOLUTE = /^(?:[a-z][a-z0-9+.-]*:|\/)/i

/** A live document at a path, as the resolver needs it. */
export interface DocTarget {
  readonly ref: string | null
  readonly image: boolean
  readonly sha256: string | null
}

/**
 * The pure resolver of a document at `fromPath`: `find` looks up a live document by path, `pageHref` builds a
 * document page's link, `imageUrl` gives an image's signed URL (null while it loads).
 */
export const makeDocLinks = (ctx: {
  readonly fromPath: string
  readonly find: (path: string) => DocTarget | undefined
  readonly pageHref: (ref: string) => string
  readonly imageUrl: (sha256: string) => string | null
}): DocLinks => ({
  resolve: (href) => {
    const h = href.trim()
    if (h === "" || h.startsWith("#") || ABSOLUTE.test(h)) return null
    const to = resolveDocPath(ctx.fromPath, h)
    if (to === null) return { kind: "missing", path: h }
    const target = ctx.find(to.path)
    if (!target || target.ref === null) return { kind: "missing", path: to.path }
    return { kind: "page", href: `${ctx.pageHref(target.ref)}${to.hash}` }
  },
  image: (src) => {
    const s = src.trim()
    if (/^https?:\/\//i.test(s)) return { kind: "remote", shown: s.replace(/^https?:\/\//i, "") }
    if (s === "" || ABSOLUTE.test(s) || s.startsWith("#")) return { kind: "missing", path: s }
    const to = resolveDocPath(ctx.fromPath, s)
    if (to === null) return { kind: "missing", path: s }
    const target = ctx.find(to.path)
    if (!target?.image || target.sha256 === null) return { kind: "missing", path: to.path }
    const url = ctx.imageUrl(target.sha256)
    return url === null ? { kind: "loading" } : { kind: "image", src: url }
  },
})

/**
 * Image sources in Markdown (`![alt](src "title")`, `<img src="…">`), for signing their URLs before the render.
 * A best effort: what it misses shows "image not imported".
 */
export const imageSourcesOf = (markdown: string): ReadonlyArray<string> => {
  const out = new Set<string>()
  for (const m of markdown.matchAll(/!\[[^\]\n]*\]\(\s*<?([^\s)>]+)>?(?:\s+["'(][^)]*)?\)/g))
    if (m[1]) out.add(m[1])
  for (const m of markdown.matchAll(/<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["']/gi)) if (m[1]) out.add(m[1])
  // reference definitions (`[shot]: shots/a.png`): an image may use one; a link's target costs only a lookup
  for (const m of markdown.matchAll(/^ {0,3}\[[^\]\n]+\]:\s*<?([^\s>]+)>?/gm)) if (m[1]) out.add(m[1])
  return [...out]
}

// ---- the marks in Markdown (a rehype plugin, before Streamdown's URL check) -----------------------------------------

/** A relative link of a document, resolved by `DocLinks` when it renders. */
export const DOC_LINK_TAG = "minga-doclink"
/** An image of a document (every `img`, Markdown or raw HTML), resolved by `DocLinks` when it renders. */
export const DOC_IMAGE_TAG = "minga-docimg"

interface HastNode {
  type: string
  tagName?: string
  value?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
}

const isRelative = (href: string): boolean => {
  const h = href.trim()
  return h !== "" && !h.startsWith("#") && !ABSOLUTE.test(h)
}

/**
 * Inside a document only: every relative `a` becomes a {@link DOC_LINK_TAG} and every `img` a {@link DOC_IMAGE_TAG}
 * mark, so Streamdown's URL check (which has no base to resolve a relative URL against) never sees them and the marks
 * resolve among the project's documents. Absolute links stay `a` and keep today's checks.
 */
export const rehypeDocMarks = () => (tree: HastNode) => {
  const visit = (node: HastNode) => {
    for (const child of node.children ?? []) {
      if (child.type === "element") {
        const props = child.properties ?? {}
        if (child.tagName === "a" && typeof props.href === "string" && isRelative(props.href)) {
          child.tagName = DOC_LINK_TAG
          child.properties = { dataHref: props.href }
        } else if (child.tagName === "img") {
          child.tagName = DOC_IMAGE_TAG
          child.properties = {
            dataSrc: typeof props.src === "string" ? props.src : "",
            dataAlt: typeof props.alt === "string" ? props.alt : "",
          }
          child.children = []
        }
      }
      visit(child)
    }
  }
  visit(tree)
}

// ---- heading anchors (GitHub-style slugs, prefixed so a heading never clobbers an id of the app) ---------------------

/** A document heading's id is this prefix plus its slug: `## Risks` → `id="doc-risks"`, reached by `#risks`. */
export const DOC_ANCHOR_PREFIX = "doc-"

/** GitHub's heading slug: lower case, punctuation dropped, spaces as `-` ("1.5 Review notes" → "15-review-notes"). */
export const docSlug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, "")
    .replace(/ /g, "-")

const textOf = (node: HastNode): string =>
  node.type === "text" ? (node.value ?? "") : (node.children ?? []).map(textOf).join("")

/**
 * Inside a document only: every `h1`-`h6` gets `id="doc-<slug>"`, deduplicated like GitHub (`risks`, `risks-1`). Runs
 * after the sanitizer, so the ids are ours, never the author's.
 */
export const rehypeDocHeadingIds = () => (tree: HastNode) => {
  const seen = new Map<string, number>()
  const visit = (node: HastNode) => {
    for (const child of node.children ?? []) {
      if (child.type === "element" && /^h[1-6]$/.test(child.tagName ?? "")) {
        const base = docSlug(textOf(child))
        const n = seen.get(base) ?? 0
        seen.set(base, n + 1)
        child.properties = {
          ...(child.properties ?? {}),
          id: `${DOC_ANCHOR_PREFIX}${n === 0 ? base : `${base}-${n}`}`,
        }
      }
      visit(child)
    }
  }
  visit(tree)
}

/** The ids a `#hash` may name, in order: its slug as written (lower case), then its slug of the text. */
export const docAnchorIds = (hash: string): ReadonlyArray<string> => {
  let raw = hash.replace(/^#/, "")
  try {
    raw = decodeURIComponent(raw)
  } catch {
    // keep it as written
  }
  if (raw === "") return []
  return [...new Set([raw.toLowerCase(), docSlug(raw)])].map((id) => `${DOC_ANCHOR_PREFIX}${id}`)
}

/** The heading a `#hash` names in the page, or null. */
export const docAnchorElement = (
  hash: string,
  root: Pick<Document, "getElementById"> = document,
): HTMLElement | null => {
  for (const id of docAnchorIds(hash)) {
    const el = root.getElementById(id)
    if (el) return el
  }
  return null
}

/** Scroll the document heading `hash` names into view; false when there is none. */
export const scrollToDocAnchor = (
  hash: string,
  root: Pick<Document, "getElementById"> = document,
): boolean => {
  const el = docAnchorElement(hash, root)
  if (!el) return false
  el.scrollIntoView({ block: "start" })
  return true
}
